import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Chip,
  type ChipProps,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';

interface FormatData {
  id: string;
  fmtType?: { name: string };
  format_type?: string;
  mediapath: string;
  filename: string;
  mimetype: string;
}

interface FinalizeData {
  asset_id: string;
  asset: {
    name: string;
    media_group?: string;
    formats?: FormatData[];
  };
}

interface QueuedItem {
  id: string;
  fileName: string;
  title: string;
  description: string;
  progress: number;
  status: 'queued' | 'uploading' | 'done';
  error: string | null;
  isFinalizing: boolean;
  finalizeData: FinalizeData | null;
}

interface MetaChanges {
  title?: string;
  description?: string;
}

interface QueuedUploaderItemProps {
  item: QueuedItem;
  onChangeMeta: (id: string, changes: MetaChanges) => void;
  onUpload: (id: string) => void;
  onRemove: (id: string) => void;
}

interface StatusChipProps extends Partial<ChipProps> {
  label: string;
  style?: React.CSSProperties;
  variant?: 'filled' | 'outlined';
}

const QueuedUploaderItem: React.FC<QueuedUploaderItemProps> = ({ item, onChangeMeta, onUpload, onRemove }) => {
  const { id, fileName, title, description, progress, status, error, isFinalizing, finalizeData } = item;

  const disabled = status === 'uploading' || isFinalizing || status === 'done';
  const isDone = status === 'done';
  const theme = useTheme();

  const statusChipProps = ((): StatusChipProps => {
    switch (true) {
      case !!error:
        return {
          label: 'error',
          style: { color: theme.palette.error.main, borderColor: theme.palette.error.main },
          variant: 'outlined',
        };
      case isFinalizing:
        return { label: 'finalizing', color: 'primary', variant: 'filled' };
      case status === 'uploading':
        return { label: 'uploading', color: 'primary', variant: 'filled' };
      case isDone:
        return { label: 'done', color: 'secondary', variant: 'filled' };
      default:
        return {
          label: 'queued',
          variant: 'outlined',
          style: { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
        };
    }
  })();

  return (
    <Paper
      elevation={0}
      style={{
        padding: 14,
        marginBottom: 12,
        opacity: isDone ? 0.7 : 1,
        borderLeft: isDone ? `3px solid ${theme.palette.secondary.main}` : '3px solid transparent',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Box>
          <Typography variant="subtitle2">{fileName}</Typography>
          <Box display="flex" alignItems="center" gap={8} mt={0.5} mb={0.5}>
            <Chip size="small" {...statusChipProps} />
            {error ? (
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                * {error}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box>
          {!isDone && (
            <Tooltip title="Upload">
              <span>
                <IconButton onClick={() => onUpload(id)} disabled={disabled} size="small" color="primary">
                  <CloudUploadIcon color="primary" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Remove">
            <IconButton onClick={() => onRemove(id)} size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box display="flex" gap={12} mt={1} flexWrap="wrap">
        <TextField
          label="Title"
          variant="outlined"
          size="small"
          value={title}
          onChange={(e) => onChangeMeta(id, { title: e.target.value })}
          disabled={disabled}
        />
        <TextField
          label="Description"
          variant="outlined"
          size="small"
          value={description}
          onChange={(e) => onChangeMeta(id, { description: e.target.value })}
          disabled={disabled}
        />
      </Box>
      {progress > 0 && progress < 100 && (
        <Box mt={1}>
          <Typography variant="caption">{progress}%</Typography>
          <LinearProgress color="secondary" variant="determinate" value={progress} />
        </Box>
      )}
      {isFinalizing && (
        <Box mt={1}>
          <Typography variant="caption">Finalizing... Please keep this tab open</Typography>
          <LinearProgress color="secondary" variant="indeterminate" />
        </Box>
      )}
      {finalizeData && (
        <Box mt={1}>
          <Typography variant="body2">
            <strong>Asset:</strong> {finalizeData?.asset?.name} (ID: {finalizeData?.asset_id})
          </Typography>
          <Typography variant="body2">
            <strong>Media Group:</strong> {finalizeData?.asset?.media_group || '\u2014'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default QueuedUploaderItem;
